import React, { useEffect, useRef, useState } from 'react'
import { PanelSegmentInput, PanelSegmentSelect } from 'components/menu/panel'
import { IFilterType } from 'components/spreadsheet/types'
import { ITableColumn } from 'types'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { formatDateTime, formatDate, toUTCString } from 'components/spreadsheet/helpers/format'
import { useFieldValueOptions } from './FieldValueMultiPicker'

// The value end of a filter's pill: the segments after its condition. What they are depends on the
// condition and the column: values to pick ("one of"), nothing ("is empty"), a date (today or an
// exact one), ticked or not, or a value to type.

interface Props {
  column: ITableColumn
  filterType: string
  value: string
  multipleValues: string[]
  onChange: (value: string) => void
  onChangeMultipleValues: (values: string[]) => void
  /** Open the values list straight away (a filter added from a column's own menu). */
  optionsOpen?: boolean
}

const TODAY = '{{date.today}}'

const NO_VALUE = [IFilterType.is_null, IFilterType.is_not_null, IFilterType.is_valid, IFilterType.is_not_valid]

// A rule saved without a value comes back from the server as null, so the segments are given '' instead.
type FilterValueProps = Omit<Props, 'value'> & { value: string | null | undefined }

const FilterValue: React.FC<FilterValueProps> = ({ value, ...rest }) => {
  const props: Props = { ...rest, value: value ?? '' }
  const { column, filterType } = props
  if (filterType === IFilterType.one_of || filterType === IFilterType.not_one_of) return <ValuesSegment {...props} />
  if (NO_VALUE.includes(filterType as IFilterType)) return null
  if (column.kind === 'date' || column.kind === 'datetime') return <DateSegments {...props} />
  if (column.kind === 'checkbox') {
    return (
      <PanelSegmentSelect
        ariaLabel="Value"
        options={[
          { value: 'true', label: 'ticked' },
          { value: 'false', label: 'not ticked' }
        ]}
        selected={[props.value === 'true' ? 'true' : 'false']}
        onPick={props.onChange}
        searchable={false}
      />
    )
  }
  return <TextSegment {...props} />
}

/** "one of …": the values to keep, picked from those the column holds. */
const ValuesSegment: React.FC<Props> = ({ column, multipleValues, onChangeMultipleValues, optionsOpen }) => {
  const { options: values, isLoaded } = useFieldValueOptions(column.publicId, multipleValues)
  // Rows with nothing in the column are a value too; give it a name rather than a blank row.
  const options = values.map((option) => (option.value === '' ? { ...option, label: 'Empty' } : option))
  const toggle = (option: string) =>
    onChangeMultipleValues(
      multipleValues.includes(option) ? multipleValues.filter((value) => value !== option) : [...multipleValues, option]
    )

  return (
    <PanelSegmentSelect
      ariaLabel="Values"
      options={options}
      selected={multipleValues}
      onPick={toggle}
      multiselect
      loading={!isLoaded}
      placeholder="Pick values"
      menuWidth={220}
      defaultOpen={optionsOpen}
    />
  )
}

/** A date: today (whenever the view is opened) or an exact day, typed or picked. */
const DateSegments: React.FC<Props> = ({ column, value, onChange }) => {
  const [mode, setMode] = useState(value === TODAY ? 'today' : 'exact')
  useEffect(() => setMode(value === TODAY ? 'today' : 'exact'), [value])

  const shown = () => {
    if (!value || value === TODAY) return ''
    return column.kind === 'datetime'
      ? formatDateTime(value + 'Z', 'YYYY-MM-DDTHH:mm')
      : formatDate(value, 'YYYY-MM-DD')
  }

  return (
    <>
      <PanelSegmentSelect
        ariaLabel="Date"
        options={[
          { value: 'today', label: 'today' },
          { value: 'exact', label: 'exact date' }
        ]}
        selected={[mode]}
        onPick={(option) => {
          setMode(option)
          if (option === 'today') onChange(TODAY)
        }}
        searchable={false}
      />
      {mode === 'exact' && (
        <PanelSegmentInput
          aria-label="Exact date"
          type={column.kind === 'datetime' ? 'datetime-local' : 'date'}
          value={shown()}
          onChange={(event) => onChange(toUTCString(event.target.value, column.kind))}
          // A date field is as wide as its own day / month / year parts and picker need.
          style={{ width: 'auto' }}
        />
      )}
    </>
  )
}

// A percentage is kept as a fraction (75.8% is 0.758), but typed as the percentage people see, so
// the field shows and takes 50 for 0.5. A {{variable}}, or anything that isn't a number, passes as it is.
const isNumber = (text: string) => text.trim() !== '' && !Number.isNaN(Number(text))
const roundOff = (n: number) => Number(n.toPrecision(12))
export const percentShown = (stored: string) => (isNumber(stored) ? String(roundOff(Number(stored) * 100)) : stored)
export const percentStored = (typed: string) => (isNumber(typed) ? String(roundOff(Number(typed) / 100)) : typed)

/** A value to type, sent half a second after the typing stops. A {{variable}} shows in orange. */
const TextSegment: React.FC<Props> = ({ column, value, onChange }) => {
  const percent = column.kind === 'percentage'
  const shown = percent ? percentShown(value) : value
  const [current, setCurrent] = useState(shown)
  const timeout = useRef<TimeoutID | null>(null)

  useEffect(() => setCurrent(shown), [value])

  useEffect(() => {
    if (current === shown) return
    if (timeout.current !== null) cancelTimeout(timeout.current)
    timeout.current = requestTimeout(() => onChange(percent ? percentStored(current) : current), 500)
  }, [current])

  const isVariable = current.includes('{{') && current.includes('}}')

  return (
    <>
      <PanelSegmentInput
        data-tour="filter-value-input"
        aria-label={percent ? 'Value, as a percentage' : 'Value'}
        placeholder="Value"
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        className={`${isVariable ? 'text-orange' : ''} ${percent ? 'pr-0' : ''}`}
      />
      {percent && (
        <span aria-hidden className="flex shrink-0 items-center pr-1.5 text-[13px] text-muted select-none">
          %
        </span>
      )}
    </>
  )
}

export default FilterValue
